/**
 * Redaction for pin feedback (the server repeats it: Giraffe.Application.Feedback.FeedbackRedaction). Nothing secret may
 * leave the browser inside a report: a property whose NAME looks like a secret loses its value, and secret-shaped text in
 * any string (bearer tokens, gpat_/gk_ tokens, JWTs, password=… pairs, the session and XSRF cookies, presigned
 * signatures) becomes "[redacted]". Strings are capped.
 */
export const REDACTED = "[redacted]";

const SENSITIVE_NAME_PARTS = [
  "password",
  "passwd",
  "pwd",
  "secret",
  "token",
  "authorization",
  "cookie",
  "xsrf",
  "csrf",
  "apikey",
  "sessionid",
  "credential",
  "signature",
  "privatekey",
];

/** Case, dashes and underscores ignored: "X-XSRF-TOKEN", "newPassword", "api_key" are all sensitive. */
export function isSensitiveName(name: string): boolean {
  const folded = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return SENSITIVE_NAME_PARTS.some((part) => folded.includes(part));
}

export function cap(text: string, max: number): string {
  const marker = "…[truncated]";
  return text.length <= max ? text : `${text.slice(0, Math.max(0, max - marker.length))}${marker}`;
}

const PATTERNS: [RegExp, string][] = [
  [/\b(Bearer|ApiKey|Basic)\s+[A-Za-z0-9._~+/=-]{6,}/gi, `$1 ${REDACTED}`],
  [/\b(gpat|gk)_[A-Za-z0-9_-]{6,}/g, `$1_${REDACTED}`],
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, REDACTED],
  [
    /((?:new_?|current_?|old_?)?(?:password|passwd|pwd|secret|token|api[_-]?key|access[_-]?token|refresh[_-]?token|client[_-]?secret)"?)(\s*[:=]\s*"?)[^"&\s,;}]+/gi,
    `$1$2${REDACTED}`,
  ],
  [/\b(giraffe_session|XSRF-TOKEN|\.AspNetCore\.[A-Za-z]+)=[^;\s"]+/g, `$1=${REDACTED}`],
  [/\b(X-Amz-(?:Signature|Credential|Security-Token))=[^&\s"]+/gi, `$1=${REDACTED}`],
];

export function redactText(text: string, max = 4000): string {
  let value = text;
  for (const [pattern, replacement] of PATTERNS) value = value.replace(pattern, replacement);
  return cap(value, max);
}

/** Path plus query, with the value of any secret-named query parameter replaced. Never the origin's credentials. */
export function redactUrl(raw: string, base = typeof window === "undefined" ? "http://localhost" : window.location.origin): string {
  let url: URL;
  try {
    url = new URL(raw, base);
  } catch {
    return redactText(raw, 600);
  }
  url.username = "";
  url.password = "";
  for (const name of [...url.searchParams.keys()]) {
    if (isSensitiveName(name)) url.searchParams.set(name, REDACTED);
  }
  return redactText(url.toString(), 600);
}

/** A query string ("?a=b") with the value of any secret-named parameter replaced. */
export function redactQuery(query: string): string {
  const params = new URLSearchParams(query);
  for (const name of [...params.keys()]) {
    if (isSensitiveName(name)) params.set(name, REDACTED);
  }
  const text = params.toString();
  return text ? `?${redactText(text, 600)}` : "";
}

/** A redacted, bounded deep copy (depth 12, arrays keep their newest `maxItems`). */
export function redactValue(value: unknown, depth = 0, maxItems = 100): unknown {
  if (value === null || value === undefined) return value ?? null;
  if (typeof value === "string") return redactText(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (depth >= 12) return "[too deep]";
  if (Array.isArray(value)) return value.slice(-maxItems).map((item) => redactValue(item, depth + 1, maxItems));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = isSensitiveName(key) && item !== null && item !== undefined ? REDACTED : redactValue(item, depth + 1, maxItems);
    }
    return out;
  }
  return typeof value === "bigint" ? value.toString() : "[unsupported value]";
}
